import { Fragment } from "@wordpress/element";

/**
 * External dependencies
 */
import classnames from 'classnames';
import { isInteger } from 'lodash';
import moment from 'moment';

/**
 * WordPress dependencies
 */
import { createElement, useState, useMemo, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
/**
 * Internal dependencies
 */

import Button from '../button';
import ButtonGroup from '../button-group';
import TimeZone from './timezone';
const TIMEZONELESS_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

function from12hTo24h(hours, isPm) {
  return isPm ? (hours % 12 + 12) % 24 : hours % 12;
}
/**
 * A shared component to parse, validate, and handle remounting of the
 * underlying form field element like <input> and <select>.
 */


function UpdateOnBlurAsIntegerField(_ref) {
  let {
    as,
    value,
    onUpdate,
    className,
    ...props
  } = _ref;

  function handleBlur(event) {
    const {
      target
    } = event;

    if (String(value) === target.value) {
      return;
    }

    const parsedValue = parseInt(target.value, 10); // Run basic number validation on the input.

    if (!isInteger(parsedValue) || typeof props.max !== 'undefined' && parsedValue > props.max || typeof props.min !== 'undefined' && parsedValue < props.min) {
      // If validation failed, reset the value to the previous valid value.
      target.value = String(value);
    } else {
      // Otherwise, it's valid, call onUpdate.
      onUpdate(parsedValue);
    }
  }

  return createElement(as || 'input', {
    // Re-mount the input value to accept the latest value as the defaultValue.
    key: value,
    defaultValue: value,
    onBlur: handleBlur,
    className: classnames('components-datetime__time-field-integer-field', className),
    ...props
  });
}
/**
 * TimePicker is a React component that renders a clock for time selection.
 *
 * ```jsx
 * import { TimePicker } from '@wordpress/components';
 * import { useState } from '@wordpress/element';
 *
 * const MyTimePicker = () => {
 *   const [ time, setTime ] = useState( new Date() );
 *
 *   return (
 *     <TimePicker
 *       currentTime={ date }
 *       onChange={ ( newTime ) => setTime( newTime ) }
 *       is12Hour
 *     />
 *   );
 * };
 * ```
 */


export function TimePicker(_ref2) {
  let {
    is12Hour,
    currentTime,
    onChange
  } = _ref2;
  const [date, setDate] = useState(() => // Truncate the date at the minutes, see: #15495.
  currentTime ? moment(currentTime).startOf('minutes') : moment()); // Reset the state when currentTime changed.

  useEffect(() => {
    setDate(currentTime ? moment(currentTime).startOf('minutes') : moment());
  }, [currentTime]);
  const {
    day,
    month,
    year,
    minutes,
    hours,
    am
  } = useMemo(() => ({
    day: date.format('DD'),
    month: date.format('MM'),
    year: date.format('YYYY'),
    minutes: date.format('mm'),
    hours: date.format(is12Hour ? 'hh' : 'HH'),
    am: Number(date.format('H')) <= 11 ? 'AM' : 'PM'
  }), [date, is12Hour]);
  /**
   * Function that sets the date state and calls the onChange with a new date.
   * The date is truncated at the minutes.
   *
   * @param {Moment} newDate The date object.
   */

  function changeDate(newDate) {
    setDate(newDate);
    onChange === null || onChange === void 0 ? void 0 : onChange(newDate.format(TIMEZONELESS_FORMAT));
  }

  function update(name) {
    return value => {
      // If the 12-hour format is being used and the 'PM' period is selected, then
      // the incoming value (which ranges 1-12) should be increased by 12 to match
      // the expected 24-hour format.
      let adjustedValue = value;

      if (name === 'hours' && is12Hour) {
        adjustedValue = from12hTo24h(value, am === 'PM');
      } // Clone the date and call the specific setter function according to `name`.


      const newDate = date.clone()[name](adjustedValue);
      changeDate(newDate);
    };
  }

  function updateAmPm(value) {
    return () => {
      if (am === value) {
        return;
      }

      const parsedHours = parseInt(hours, 10);
      const newDate = date.clone().hours(from12hTo24h(parsedHours, value === 'PM'));
      changeDate(newDate);
    };
  }

  const dayFormat = createElement("div", {
    className: "components-datetime__time-field components-datetime__time-field-day"
  }, createElement(UpdateOnBlurAsIntegerField, {
    "aria-label": __('Day'),
    className: "components-datetime__time-field-day-input",
    type: "number" // The correct function to call in moment.js is "date" not "day".
    ,
    name: "date",
    value: day,
    step: 1,
    min: 1,
    max: 31,
    onUpdate: update('date')
  }));
  const monthFormat = createElement("div", {
    className: "components-datetime__time-field components-datetime__time-field-month"
  }, createElement(UpdateOnBlurAsIntegerField, {
    as: "select",
    "aria-label": __('Month'),
    className: "components-datetime__time-field-month-select",
    name: "month",
    value: month // The value starts from 0, so we have to -1 when setting month.
    ,
    onUpdate: value => update('month')(value - 1)
  }, createElement("option", {
    value: "01"
  }, __('January')), createElement("option", {
    value: "02"
  }, __('February')), createElement("option", {
    value: "03"
  }, __('March')), createElement("option", {
    value: "04"
  }, __('April')), createElement("option", {
    value: "05"
  }, __('May')), createElement("option", {
    value: "06"
  }, __('June')), createElement("option", {
    value: "07"
  }, __('July')), createElement("option", {
    value: "08"
  }, __('August')), createElement("option", {
    value: "09"
  }, __('September')), createElement("option", {
    value: "10"
  }, __('October')), createElement("option", {
    value: "11"
  }, __('November')), createElement("option", {
    value: "12"
  }, __('December'))));
  const dayMonthFormat = is12Hour ? createElement(Fragment, null, monthFormat, dayFormat) : createElement(Fragment, null, dayFormat, monthFormat);
  return createElement("div", {
    className: classnames('components-datetime__time')
  }, createElement("fieldset", null, createElement("legend", {
    className: "components-datetime__time-legend invisible"
  }, __('Date')), createElement("div", {
    className: "components-datetime__time-wrapper"
  }, dayMonthFormat, createElement("div", {
    className: "components-datetime__time-field components-datetime__time-field-year"
  }, createElement(UpdateOnBlurAsIntegerField, {
    "aria-label": __('Year'),
    className: "components-datetime__time-field-year-input",
    type: "number",
    name: "year",
    step: 1,
    min: 0,
    max: 9999,
    value: year,
    onUpdate: update('year')
  })))), createElement("fieldset", null, createElement("legend", {
    className: "components-datetime__time-legend invisible"
  }, __('Time')), createElement("div", {
    className: "components-datetime__time-wrapper"
  }, createElement("div", {
    className: "components-datetime__time-field components-datetime__time-field-time"
  }, createElement(UpdateOnBlurAsIntegerField, {
    "aria-label": __('Hours'),
    className: "components-datetime__time-field-hours-input",
    type: "number",
    name: "hours",
    step: 1,
    min: is12Hour ? 1 : 0,
    max: is12Hour ? 12 : 23,
    value: hours,
    onUpdate: update('hours')
  }), createElement("span", {
    className: "components-datetime__time-separator",
    "aria-hidden": "true"
  }, ":"), createElement(UpdateOnBlurAsIntegerField, {
    "aria-label": __('Minutes'),
    className: "components-datetime__time-field-minutes-input",
    type: "number",
    name: "minutes",
    step: 1,
    min: 0,
    max: 59,
    value: minutes,
    onUpdate: update('minutes')
  })), is12Hour && createElement(ButtonGroup, {
    className: "components-datetime__time-field components-datetime__time-field-am-pm"
  }, createElement(Button, {
    variant: am === 'AM' ? 'primary' : 'secondary',
    onClick: updateAmPm('AM'),
    className: "components-datetime__time-am-button"
  }, __('AM')), createElement(Button, {
    variant: am === 'PM' ? 'primary' : 'secondary',
    onClick: updateAmPm('PM'),
    className: "components-datetime__time-pm-button"
  }, __('PM'))), createElement(TimeZone, null))));
}
export default TimePicker;
//# sourceMappingURL=time.js.map